export type ClaimIntakeVoiceFormSchemaField = {
  label: string;
  key: string;
  required: boolean;
  type: string;
  help?: string;
};

export type ClaimIntakeVoiceEventDef = {
  type: 'event';
  name: string;
  help: string;
};

type VoiceFormFieldDef = {
  path: string;
  help?: string;
  required?: boolean;
};

/**
 * Labels describe the field inside the claim while keys contain the complete
 * payload path expected by form capture.
 */
function voiceFormField({
  path,
  help,
  required = true,
}: VoiceFormFieldDef): ClaimIntakeVoiceFormSchemaField {
  return {
    label: path,
    key: `claim.${path}`,
    required,
    type: 'string',
    ...(help ? { help } : {}),
  };
}

const OPTIONAL_ASK =
  'Optional — still ask the caller once, say it is optional, and allow them to skip.';

const VOICE_FORM_FIELD_DEFS: VoiceFormFieldDef[] = [
  {
    path: 'policies.policy_id',
    required: false,
    help: 'Warranty contract number. Ask for VIN, contract number, or both. At least one of VIN or contract number is required.',
  },
  {
    path: 'product.product_id',
    required: false,
    help: 'VIN: full 17 characters or last 8 characters. Ask for VIN, contract number, or both. At least one of VIN or contract number is required.',
  },
  { path: 'vehicle_arrival_method', help: 'Allowed values: driven or towed' },
  { path: 'vehicle_at_service_center', help: 'Allowed values: yes or no' },
  {
    path: 'reimbursement_method',
    help: 'Ask the caller "Check or Credit Card" only. Never say CONNEXPAY aloud. If they choose Credit Card, store the value CONNEXPAY. If Check, store Check.',
  },
  {
    path: 'payment_email',
    required: false,
    help: `Required only when reimbursement_method is CONNEXPAY (Credit Card). ${OPTIONAL_ASK} when reimbursement is Check.`,
  },
  { path: 'failure_odometer', help: 'Vehicle mileage at intake' },
  { path: 'invoice_number' },
  { path: 'repair_order_date', help: 'Date in MM/DD/YYYY format' },
  { path: 'labor_rate', help: 'Hourly labor rate as a number' },

  { path: 'customer.name' },
  { path: 'customer.phone_no' },
  { path: 'customer.address.address1' },
  {
    path: 'customer.address.address2',
    required: false,
    help: OPTIONAL_ASK,
  },
  {
    path: 'customer.address.city',
    required: false,
    help: OPTIONAL_ASK,
  },
  {
    path: 'customer.address.state',
    required: false,
    help: OPTIONAL_ASK,
  },
  { path: 'customer.address.postal_code' },
  {
    path: 'customer.address.country_code',
    required: false,
    help: OPTIONAL_ASK,
  },

  { path: 'servicer.name' },
  {
    path: 'servicer.email',
    required: false,
    help: `${OPTIONAL_ASK} Separate multiple addresses with a semicolon (;). Example: servicer@carspp.com; manager@carspp.com`,
  },
  { path: 'servicer.phone_no' },
  {
    path: 'servicer.contact_name',
    required: false,
    help: OPTIONAL_ASK,
  },
  {
    path: 'servicer.mobile_no',
    required: false,
    help: OPTIONAL_ASK,
  },
  {
    path: 'servicer.want_text',
    required: false,
    help: `Allowed values: yes or no. ${OPTIONAL_ASK}`,
  },
  { path: 'servicer.address.address1' },
  {
    path: 'servicer.address.address2',
    required: false,
    help: OPTIONAL_ASK,
  },
  {
    path: 'servicer.address.city',
    required: false,
    help: OPTIONAL_ASK,
  },
  {
    path: 'servicer.address.state',
    required: false,
    help: OPTIONAL_ASK,
  },
  { path: 'servicer.address.postal_code' },
  {
    path: 'servicer.address.country_code',
    required: false,
    help: OPTIONAL_ASK,
  },
  {
    path: 'servicer.dealer_no',
    required: false,
    help: OPTIONAL_ASK,
  },

  {
    path: 'vehicle_notes.parts_labor_warranty_months',
    help: 'Parts and labor warranty months. Whole number greater than 0, or N/A, or Unknown.',
  },
  {
    path: 'vehicle_notes.parts_labor_warranty_miles',
    help: 'Parts and labor warranty miles. Whole number greater than 0, or N/A, or Unknown.',
  },
  {
    path: 'vehicle_notes.is_vehicle_modified',
    help: 'Allowed values: yes or no',
  },
  {
    path: 'vehicle_notes.tire_type',
    required: false,
    help: `Allowed values: P, LT, EURO, T, or ST. Collect when the vehicle is modified. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.tire_section_width_mm',
    required: false,
    help: `Collect when the vehicle is modified. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.tire_aspect_ratio',
    required: false,
    help: `Collect when the vehicle is modified. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.tire_wheel_diameter_in',
    required: false,
    help: `Collect when the vehicle is modified. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.oem_tire_section_width_mm',
    required: false,
    help: `Collect when the vehicle is modified. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.oem_tire_aspect_ratio',
    required: false,
    help: `Collect when the vehicle is modified. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.oem_tire_wheel_diameter_in',
    required: false,
    help: `Collect when the vehicle is modified. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.has_lift_kit',
    required: false,
    help: `Allowed values: yes or no. Collect when the vehicle is modified. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.lift_amount_inches',
    required: false,
    help: `Collect when has_lift_kit is yes. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.has_engine_modifications',
    required: false,
    help: `Allowed values: yes or no. Collect when the vehicle is modified. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.engine_modification_description',
    required: false,
    help: `Collect when has_engine_modifications is yes. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.coolant_level',
    required: false,
    help: `Allowed values: full, low, or na. ${OPTIONAL_ASK}`,
  },
  {
    path: 'vehicle_notes.coolant_condition',
    required: false,
    help: OPTIONAL_ASK,
  },
  {
    path: 'vehicle_notes.engine_oil_level',
    required: false,
    help: `Allowed values: full, low, or na. ${OPTIONAL_ASK}`,
  },
  {
    path: 'vehicle_notes.engine_oil_condition',
    required: false,
    help: OPTIONAL_ASK,
  },
  {
    path: 'vehicle_notes.transmission_fluid_level',
    required: false,
    help: `Allowed values: full, low, or na. ${OPTIONAL_ASK}`,
  },
  {
    path: 'vehicle_notes.transmission_fluid_condition',
    required: false,
    help: OPTIONAL_ASK,
  },
  { path: 'vehicle_notes.has_leaks', help: 'Allowed values: yes or no' },
  {
    path: 'vehicle_notes.leak_location',
    required: false,
    help: `Collect when has_leaks is yes. ${OPTIONAL_ASK} otherwise.`,
  },
  {
    path: 'vehicle_notes.leak_drip_condition',
    required: false,
    help: `Allowed values: drip or seepage. Collect when has_leaks is yes. ${OPTIONAL_ASK} otherwise.`,
  },
  { path: 'vehicle_notes.has_warning_light', help: 'Allowed values: yes or no' },
  {
    path: 'vehicle_notes.codes_and_definitions',
    required: false,
    help: `Collect when has_warning_light is yes. ${OPTIONAL_ASK} otherwise.`,
  },

  { path: 'requests.0.complaint_description' },
  { path: 'requests.0.cause_description' },
  { path: 'requests.0.corrective_action_description' },
  {
    path: 'requests.0.tiretread',
    required: false,
    help: `Tire tread depth from 0 to 32 for tire claims. ${OPTIONAL_ASK} otherwise.`,
  },
  { path: 'requests.0.labor.0.labor_description' },
  {
    path: 'requests.0.labor.0.labor_opcode',
    required: false,
    help: OPTIONAL_ASK,
  },
  { path: 'requests.0.labor.0.labor_hours' },
  {
    path: 'requests.0.parts.0.part_number',
    required: false,
    help: OPTIONAL_ASK,
  },
  { path: 'requests.0.parts.0.part_description' },
  { path: 'requests.0.parts.0.part_quantity' },
  { path: 'requests.0.parts.0.unit_cost' },
];

export const CLAIM_INTAKE_VOICE_FORM_FIELDS: ClaimIntakeVoiceFormSchemaField[] =
  VOICE_FORM_FIELD_DEFS.map(voiceFormField);

/** UI / agent control events emitted by the voice agent over the data channel. */
export const CLAIM_INTAKE_VOICE_EVENT_DEFS: ClaimIntakeVoiceEventDef[] = [
  {
    type: 'event',
    name: 'fetch_contract',
    help: 'Emit after VIN and/or contract number are confirmed and submitted via form capture. Frontend fills inputs, runs Lookup Contract (same as the button), and replies with contract_lookup_result. Aliases: lookup_contract, contract_lookup. Wait for contract_lookup_result before saying the contract was fetched or moving on.',
  },
  {
    type: 'event',
    name: 'select_contract',
    help: 'Emit when multiple contracts were returned and the caller chose one. Include payload.contract_number or payload.index (0-based).',
  },
  {
    type: 'event',
    name: 'go_to_next_page',
    help: 'After the caller agrees to continue to the form, emit this event. From Contract Lookup after a successful fetch, this opens the Repairs tab (step 2 / third tab) so the agent can collect remaining claim fields. Otherwise advances one step. Steps: 0 Contract Lookup, 1 Vehicle & Claims, 2 Repairs, 3 Review.',
  },
  {
    type: 'event',
    name: 'go_to_repairs_page',
    help: 'Alias of continuing to the claim form: opens Repairs (step 2 / third tab). Prefer go_to_next_page after the caller confirms. Aliases: go_to_third_tab, continue_to_form, open_repairs.',
  },
  {
    type: 'event',
    name: 'go_to_previous_page',
    help: 'Go back one claim-intake step.',
  },
  {
    type: 'event',
    name: 'submit_claim',
    help: 'Submit the claim from the Review step when the caller confirms submission.',
  },
];

export const CLAIM_INTAKE_VOICE_EVENT_NAMES = [
  'fetch_contract',
  'select_contract',
  'go_to_next_page',
  'go_to_repairs_page',
  'go_to_previous_page',
  'submit_claim',
] as const;

export type ClaimIntakeVoiceEventName = (typeof CLAIM_INTAKE_VOICE_EVENT_NAMES)[number];

const CLAIM_INTAKE_VOICE_EXTRA = {
  add_on_prompt: {
    form_schema: {
      fields: CLAIM_INTAKE_VOICE_FORM_FIELDS,
    },
    events: CLAIM_INTAKE_VOICE_EVENT_DEFS,
  },
} as const;

/** Lead `web_RTC` — `customer_details` with claim intake form_schema + events. */
export function buildClaimIntakeVoiceCustomerDetails(): {
  extra: typeof CLAIM_INTAKE_VOICE_EXTRA;
} {
  return { extra: CLAIM_INTAKE_VOICE_EXTRA };
}
